import { kebabCase } from 'lodash';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';
import { pascalCase } from 'utils/pascal-case';
import { unreachable } from 'utils/unreachable';
import { MultiSelectField } from '../fields';
import {
  SelectFilterOperationType,
  valueFromString,
  valueToString,
} from './select';
import { TermSeparator } from './separators';
import { FieldFilter } from '.';

interface MultiSelectFieldFilterBase {
  field: MultiSelectField;
}

interface MultiSelectFieldValueFilterBase<T = number>
  extends MultiSelectFieldFilterBase {
  value?: T[];
}

interface MultiSelectIsFilter<T> extends MultiSelectFieldValueFilterBase<T> {
  type: SelectFilterOperationType.Is;
}

interface MultiSelectIsNotFilter<T> extends MultiSelectFieldValueFilterBase<T> {
  type: SelectFilterOperationType.IsNot;
}

interface MultiSelectIsOneOfFilter<T>
  extends MultiSelectFieldValueFilterBase<T> {
  type: SelectFilterOperationType.IsOneOf;
}

interface MultiSelectIsNoneOfFilter<T>
  extends MultiSelectFieldValueFilterBase<T> {
  type: SelectFilterOperationType.IsNoneOf;
}

interface MultiSelectIsEmptyFilter extends MultiSelectFieldFilterBase {
  type: SelectFilterOperationType.IsEmpty;
}

interface MultiSelectIsNotEmptyFilter extends MultiSelectFieldFilterBase {
  type: SelectFilterOperationType.IsNotEmpty;
}

export type MultiSelectFieldFilter<T = number> =
  | MultiSelectIsFilter<T>
  | MultiSelectIsNotFilter<T>
  | MultiSelectIsOneOfFilter<T>
  | MultiSelectIsNoneOfFilter<T>
  | MultiSelectIsEmptyFilter
  | MultiSelectIsNotEmptyFilter;

const isValid = (filter: MultiSelectFieldFilter) => {
  switch (filter.type) {
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return (filter.value ?? []).length > 0;
    default:
      return true;
  }
};

export const isOptional = (type: SelectFilterOperationType) =>
  type === SelectFilterOperationType.IsEmpty ||
  type === SelectFilterOperationType.IsNotEmpty;

const to = valueToString(<T>(x: T): string => {
  if (typeof x === 'number') {
    return x.toString();
  }

  return String(x);
});

const from = valueFromString((x) => {
  const result = parseInt(x, 10);

  return Number.isNaN(result) || Number.isNaN(Number(x))
    ? (x as unknown as number)
    : result;
});

const toString = (filter: MultiSelectFieldFilter): string => {
  const type = kebabCase(filter.type);

  switch (filter.type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return type;
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsNoneOf:
    case SelectFilterOperationType.IsOneOf:
      return [type, to(filter.value ?? [])].join(TermSeparator);
    default:
      return unreachable(filter);
  }
};

const parse = (
  value: string,
  field: MultiSelectField,
): MultiSelectFieldFilter => {
  const [type_string, meta] = value.split(TermSeparator);
  const type = pascalCase(type_string) as SelectFilterOperationType;

  switch (type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return { field, type };
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return { field, type, value: from(meta) };
    case SelectFilterOperationType.IsAtLeast:
    case SelectFilterOperationType.IsAtMost:
    default:
      return unreachable(
        `${type} cannot be parsed for ${field.type} field` as never,
      );
  }
};

export const MultiSelectFieldFilter = {
  isValid,
  toString,
  parse,
};

export const isMultiSelectFieldFilter = (
  arg: FieldFilter,
): arg is MultiSelectFieldFilter =>
  arg.field.fieldType === FieldTypes.MultiSelect;

export const isMultiSelectFilterWithValue = <T>(
  arg: MultiSelectFieldFilter<T>,
): arg is
  | MultiSelectIsFilter<T>
  | MultiSelectIsNotFilter<T>
  | MultiSelectIsOneOfFilter<T>
  | MultiSelectIsNoneOfFilter<T> =>
  [
    SelectFilterOperationType.Is,
    SelectFilterOperationType.IsNot,
    SelectFilterOperationType.IsNoneOf,
    SelectFilterOperationType.IsOneOf,
  ].includes(arg.type);
